import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Pagination
} from '@mui/material';
import {
  Download,
  Business,
  CalendarToday,
  Search,
  Lock
} from '@mui/icons-material';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { tenderAPI, productAPI } from '../services/api';

const Tenders = () => {
  const navigate = useNavigate();
  const [tenders, setTenders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Check if user is logged in
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchTenders();
  }, [page, categoryFilter, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await productAPI.getCategories();
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchTenders = async () => {
    setLoading(true);
    setError('');
    setAccessDenied(false);

    try {
      const params = {
        page,
        limit: 12
      };

      if (categoryFilter) {
        params.category_id = categoryFilter;
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await tenderAPI.getTenders(params);
      if (response.data.success) {
        setTenders(response.data.data.tenders);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setAccessDenied(true);
        setError('Please login to view tenders.');
      } else if (err.response?.status === 403) {
        setAccessDenied(true);
        setError(err.response?.data?.message || 'Only active buyers can view tenders.');
      } else {
        setError('Failed to fetch tenders');
      }
      console.error('Error fetching tenders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchTenders();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpired = (lastDate) => {
    if (!lastDate) return false;
    return new Date(lastDate) < new Date();
  };

  return (
    <>
      <Header />

      <Container maxWidth="lg">
        <Box py={4}>
          <Typography variant="h4" gutterBottom>
            Tender Opportunities
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Browse and download the latest tender documents for your business
          </Typography>

          {/* Access Denied Message */}
          {accessDenied && (
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center', mb: 3, bgcolor: 'warning.light' }}>
              <Lock sx={{ fontSize: 60, color: 'warning.dark', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="warning.dark">
                Access Restricted
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {!isLoggedIn
                  ? 'Please login as a buyer to view tender opportunities.'
                  : 'Only active buyers can access tender information. Please ensure your account is active and you are registered as a buyer.'}
              </Typography>
              {!isLoggedIn ? (
                <Box display="flex" gap={2} justifyContent="center">
                  <Button variant="contained" onClick={() => navigate('/login')}>
                    Login
                  </Button>
                  <Button variant="outlined" onClick={() => navigate('/register')}>
                    Register as Buyer
                  </Button>
                </Box>
              ) : (
                <Button variant="contained" onClick={() => navigate('/profile')}>
                  View Profile
                </Button>
              )}
            </Paper>
          )}

          {!accessDenied && (
            <>

          {/* Filters */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <form onSubmit={handleSearch}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search tenders..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      )
                    }}
                  />
                </form>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setPage(1);
                    }}
                    label="Filter by Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setCategoryFilter('');
                    setSearchQuery('');
                    setPage(1);
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : tenders.length === 0 ? (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No tenders found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {categoryFilter || searchQuery
                  ? 'Try adjusting your filters or search query'
                  : 'Check back later for new tender opportunities'}
              </Typography>
            </Paper>
          ) : (
            <>
              <Grid container spacing={3}>
                {tenders.map((tender) => (
                  <Grid item xs={12} md={6} key={tender.id}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Typography variant="h6" component="div" sx={{ flex: 1 }}>
                            {tender.title}
                          </Typography>
                          {isExpired(tender.last_date) && (
                            <Chip label="Expired" size="small" color="error" />
                          )}
                        </Box>

                        {tender.organization && (
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Business fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {tender.organization}
                            </Typography>
                          </Box>
                        )}

                        {tender.last_date && (
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <CalendarToday fontSize="small" color="action" />
                            <Typography variant="body2" color={isExpired(tender.last_date) ? 'error' : 'text.secondary'}>
                              Last Date: {formatDate(tender.last_date)}
                            </Typography>
                          </Box>
                        )}

                        {tender.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {tender.description.length > 150
                              ? `${tender.description.substring(0, 150)}...`
                              : tender.description}
                          </Typography>
                        )}

                        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                          {tender.category && (
                            <Chip
                              label={tender.category.name}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {tender.tender_number && (
                            <Chip
                              label={`#${tender.tender_number}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>

                        {tender.document_url && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Download />}
                            component="a"
                            href={`${process.env.REACT_APP_BASE_URL || 'http://localhost:5000'}${tender.document_url.startsWith('/') ? tender.document_url : '/' + tender.document_url}`}
                            target="_blank"
                            download
                          >
                            Download Document
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={4}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </>
          )}
        </Box>
      </Container>
      <Footer />
    </>
  );
};

export default Tenders;
